import { combineReducers, configureStore, Middleware } from "@reduxjs/toolkit";
import cartReducer from "./slices/cartSlice";
import wishlistReducer from "./slices/wishlistSlice";
import userReducer from "./slices/userSlice";
import vendorReducer from "./slices/vendorSlice";
import SettingsReducer from "./slices/settingsSlice";
import { persistReducer, persistStore, type PersistedState } from "redux-persist";
import storage from "redux-persist/lib/storage";
import sessionReducer from "./slices/sessionSlice";
import notificationSlice from "./slices/notificationSlice";
import { encryptTransform } from 'redux-persist-transform-encrypt';
import { setSentryUser, clearSentryUser } from "@/utils/sentryUser";
import { migratePersistedCustomerCareState } from "@/components/CustomerSupport/customerCareState";

/**
 * Middleware that keeps Sentry user context in sync with Redux auth state.
 *
 * It fires on every login (setUser / setVendor) and logout
 * (logout / logoutVendor) action so that errors are always attributed to the
 * correct identity.  Only the opaque database _id is sent — never tokens,
 * emails, passwords, or any other sensitive field.
 */
const sentryUserMiddleware: Middleware = () => (next) => (action) => {
  const result = next(action);
  const type = (action as { type: string }).type;

  if (type === "user/setUser") {
    const a = action as { payload: { user: { _id: string } } };
    setSentryUser(a.payload.user._id);
  } else if (type === "vendor/setVendor") {
    const a = action as { payload: { vendor: { _id: string } } };
    setSentryUser(a.payload.vendor._id);
  } else if (type === "user/logout" || type === "vendor/logoutVendor") {
    clearSentryUser();
  }

  return result;
};


const secretKey = import.meta.env.VITE_ENCRYPT_KEY || (import.meta.env.DEV ? "my-super-secret-key-that-no-one-can-guess" : undefined);
if (!secretKey) {
  throw new Error("VITE_ENCRYPT_KEY is missing from environment variables");
}

const encryptor = encryptTransform({
  secretKey,
  onError: function (error) {
    console.error("Encryption error: ", error);
  },
});

const persistConfig = {
  key: "root",
  version: 1,
  migrate: (state: PersistedState) => Promise.resolve(migratePersistedCustomerCareState(state)),
  storage,
  whitelist: ["cart", "wishlist", "user", "vendor", "settings", "session", "notifications"],
  transforms: [encryptor],
};

const rootReducer = combineReducers({
  cart: cartReducer,
  wishlist: wishlistReducer,
  user: userReducer,
  vendor: vendorReducer,
  settings: SettingsReducer,
  session: sessionReducer,
  notifications: notificationSlice
});

const persistedReducer = persistReducer<ReturnType<typeof rootReducer>>(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  devTools: import.meta.env.MODE !== "production", // This hides it in production
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          "persist/PERSIST",
          "persist/REHYDRATE",
          "persist/REGISTER",
          "persist/PAUSE",
          "persist/PURGE",
          "persist/FLUSH",
        ],
      },
    }).concat(sentryUserMiddleware),
});

export const persistor = persistStore(store);
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;
export default store;
